/**
 * Sealing personal data at rest — AES-GCM-256 over JSON
 * (`specs/offline-attendance-v1/implementation-plan.md`, decision D5, task FE-3).
 *
 * Every offline-attendance row that carries personal data (event times, the
 * clock-out note, receipts, snapshot times, the grant token, the identity) is
 * stored as a {@link SealedBlob}: `JSON.stringify(value)` encrypted under the
 * installation's data key (`offline-keys.ts`) with a fresh random 12-byte IV,
 * and the ROW'S IDENTITY as additional authenticated data —
 * `syntry-offline|v1|<table>|<scopeKey>|<rowId>` ({@link sealAad}). A blob
 * therefore opens only for the row it was written for: copying one row's
 * ciphertext into another row, another table or another scope fails
 * authentication instead of decrypting to the wrong person's punch.
 *
 * What this is NOT: protection from code running on the same origin (it can
 * call these functions with the same key). It is at-rest protection for what
 * IndexedDB keeps on the device.
 *
 * Pure module: WebCrypto only, no storage, no Vue, no network.
 */
import type { SealedBlob } from '@/types/offline-attendance';

/** A data key as the sealing functions need it: the CryptoKey and the id recorded in every blob. */
export interface SealingKey {
  key: CryptoKey;
  keyId: string;
}

/** Why a blob could not be opened. */
export type SealedDataErrorKind = 'key_mismatch' | 'corrupt';

/** Fixed head of every AAD (decision D5). */
const AAD_PREFIX = 'syntry-offline|v1|';
/** Separates the AAD components — so no component may contain it. */
const AAD_SEPARATOR = '|';
/** AES-GCM nonce length: 96 bits, the size GCM is specified for. */
const IV_BYTES = 12;
/** AES-GCM authentication tag: 128 bits (the WebCrypto default, stated explicitly). */
const TAG_BYTES = 16;

const DEFAULT_MESSAGES: Record<SealedDataErrorKind, string> = {
  key_mismatch: 'The sealed data was written under a different data key.',
  corrupt: 'The sealed data is corrupt or does not belong to this record.',
};

/**
 * A blob that cannot be opened. `key_mismatch`: it names a different key id
 * than the key offered (decided BEFORE any decryption — the key was rotated or
 * lost). `corrupt`: malformed, tampered with, sealed for another row (AAD), or
 * not JSON. The message never contains plaintext.
 */
export class SealedDataError extends Error {
  constructor(
    public readonly kind: SealedDataErrorKind,
    message?: string,
  ) {
    super(message ?? DEFAULT_MESSAGES[kind]);
    this.name = 'SealedDataError';
  }
}

/**
 * The additional authenticated data that binds a blob to its row:
 * UTF-8 of `syntry-offline|v1|<table>|<scopeKey>|<rowId>`.
 *
 * Every component must be a non-empty string free of `|` — a separator inside
 * a component would let two different rows share one AAD (`a|b` + `c` versus
 * `a` + `b|c`), so it is refused with a `TypeError` rather than encoded.
 */
export function sealAad(table: string, scopeKey: string, rowId: string): Uint8Array {
  const parts: ReadonlyArray<readonly [string, unknown]> = [
    ['table', table],
    ['scopeKey', scopeKey],
    ['rowId', rowId],
  ];
  for (const [label, part] of parts) {
    if (typeof part !== 'string' || part.length === 0 || part.includes(AAD_SEPARATOR)) {
      throw new TypeError(`sealAad: ${label} must be a non-empty string without "${AAD_SEPARATOR}"`);
    }
  }
  return new TextEncoder().encode(`${AAD_PREFIX}${table}${AAD_SEPARATOR}${scopeKey}${AAD_SEPARATOR}${rowId}`);
}

/**
 * Seal `value` for one row: `{ v: 1, keyId, iv, data }` where `data` is the
 * AES-GCM ciphertext (tag appended) of `JSON.stringify(value)`.
 *
 * Throws a `TypeError` for a value JSON cannot represent (`undefined`, a
 * function, a symbol, a BigInt, a cycle) — it would otherwise be sealed as
 * something else and come back different. WebCrypto failures propagate.
 */
export async function sealJson(key: SealingKey, aad: Uint8Array, value: unknown): Promise<SealedBlob> {
  const json: string | undefined = JSON.stringify(value);
  if (typeof json !== 'string') {
    throw new TypeError('sealJson: the value cannot be represented as JSON');
  }
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const data = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: aad, tagLength: TAG_BYTES * 8 },
    key.key,
    new TextEncoder().encode(json),
  );
  return { v: 1, keyId: key.keyId, iv, data };
}

/**
 * Open a blob sealed by {@link sealJson} for the same row (`aad`).
 *
 * Throws {@link SealedDataError}: `key_mismatch` when the blob names another
 * key id; `corrupt` when it is malformed, fails authentication (tampered, the
 * wrong row's AAD, or different key material under the same id) or is not
 * JSON. `T` is the caller's assertion — the plaintext is whatever was sealed,
 * so a caller that needs certainty validates the result.
 */
export async function unsealJson<T>(key: SealingKey, aad: Uint8Array, blob: SealedBlob): Promise<T> {
  if (!isSealedBlob(blob)) {
    throw new SealedDataError('corrupt', 'The sealed data is malformed.');
  }
  if (blob.keyId !== key.keyId) {
    throw new SealedDataError('key_mismatch');
  }

  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: blob.iv, additionalData: aad, tagLength: TAG_BYTES * 8 },
      key.key,
      blob.data,
    );
  } catch (err) {
    // AES-GCM refused the blob (OperationError): tampered bytes, another row's
    // AAD, or other key material under the same id. Only the error's name is
    // kept — never the bytes.
    throw new SealedDataError('corrupt', `The sealed data failed authentication (${errorName(err)}).`);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(plaintext));
  } catch {
    // Authentic but not UTF-8 JSON: something other than sealJson() wrote it.
    throw new SealedDataError('corrupt', 'The sealed data is not valid JSON.');
  }
  return parsed as T;
}

/**
 * Structural check of a stored blob: version 1, a non-empty key id, a 12-byte
 * IV and an ArrayBuffer at least one authentication tag long. Survives a
 * structured clone (IndexedDB) and cross-realm objects.
 */
export function isSealedBlob(value: unknown): value is SealedBlob {
  if (typeof value !== 'object' || value === null) return false;
  const keyId: unknown = Reflect.get(value, 'keyId');
  const iv: unknown = Reflect.get(value, 'iv');
  const data: unknown = Reflect.get(value, 'data');
  return (
    Reflect.get(value, 'v') === 1 &&
    typeof keyId === 'string' &&
    keyId.length > 0 &&
    isUint8Array(iv) &&
    iv.byteLength === IV_BYTES &&
    isArrayBuffer(data) &&
    data.byteLength >= TAG_BYTES
  );
}

function isUint8Array(value: unknown): value is Uint8Array {
  return Object.prototype.toString.call(value) === '[object Uint8Array]';
}

function isArrayBuffer(value: unknown): value is ArrayBuffer {
  return Object.prototype.toString.call(value) === '[object ArrayBuffer]';
}

function errorName(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const name: unknown = Reflect.get(err, 'name');
    if (typeof name === 'string' && name.length > 0) return name;
  }
  return 'UnknownError';
}
